// Security utilities for input validation and sanitization

/**
 * Sanitizes input to prevent XSS attacks
 */
export function sanitizeInput(input: string): string {
  if (!input) return "";
  
  return input
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;")
    .trim();
}

/**
 * Validates email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates phone number format
 */
export function isValidPhone(phone: string): boolean {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
}

/**
 * Validates and sanitizes form data
 */
export function validateAndSanitizeFormData(data: Record<string, any>): {
  isValid: boolean;
  errors: string[];
  sanitizedData: Record<string, any>;
} {
  const errors: string[] = [];
  const sanitizedData: Record<string, any> = {};

  // Sanitize all string fields
  Object.keys(data).forEach(key => {
    if (typeof data[key] === 'string') {
      sanitizedData[key] = sanitizeInput(data[key]);
    } else {
      sanitizedData[key] = data[key];
    }
  });

  // Validate required fields
  if (!sanitizedData.name?.trim()) {
    errors.push("Name is required");
  }

  if (!sanitizedData.email?.trim()) {
    errors.push("Email is required");
  } else if (!isValidEmail(sanitizedData.email)) {
    errors.push("Invalid email format");
  }

  if (!sanitizedData.phone?.trim()) {
    errors.push("Phone number is required");
  } else if (!isValidPhone(sanitizedData.phone)) {
    errors.push("Invalid phone number format");
  }

  return {
    isValid: errors.length === 0,
    errors,
    sanitizedData
  };
}

/**
 * Rate limiting helper (simple in-memory implementation)
 */
class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  private readonly windowMs: number;
  private readonly maxRequests: number;

  constructor(windowMs: number = 60000, maxRequests: number = 5) {
    this.windowMs = windowMs;
    this.maxRequests = maxRequests;
  }

  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const requests = this.requests.get(identifier) || [];
    
    // Remove old requests outside the window
    const validRequests = requests.filter(time => now - time < this.windowMs);
    
    if (validRequests.length >= this.maxRequests) {
      return false;
    }

    // Add current request
    validRequests.push(now);
    this.requests.set(identifier, validRequests);
    
    return true;
  }
}

export const appointmentRateLimiter = new RateLimiter(300000, 3); // 3 requests per 5 minutes